import * as chains from "viem/chains"
import * as customChains from "./chains"

export const BETA_NETWORKS: number[] = [
  customChains.ink.id,
  customChains.hyperevm.id,
  chains.mantle.id,
]

export const MAIN_CHAIN_IDS = [
  chains.mainnet.id,
  chains.base.id,
  customChains.swell.id,
  customChains.sonic.id,
  customChains.bob.id,
  customChains.berachain.id,
  customChains.unichain.id,
  chains.avalanche.id,
  chains.arbitrum.id,
  chains.bsc.id,
  customChains.tac.id,
  chains.linea.id,
  customChains.plasma.id,
  customChains.monad.id,
] as const

export const SUPPORTED_CHAINS = [
  chains.mainnet,
  chains.base,
  chains.arbitrum,
  customChains.swell,
  customChains.bartio,
  chains.sepolia,
  chains.foundry,
  customChains.tenderly,
  customChains.sonic,
  customChains.berachain,
  customChains.ink,
  customChains.unichain,
  chains.avalanche,
  customChains.bob,
  chains.bsc,
  customChains.hyperevm,
  customChains.tac,
  chains.linea,
  chains.mantle,
  customChains.plasma,
  customChains.monad,
]

export const SUPPORTED_CHAIN_IDS = [
  ...MAIN_CHAIN_IDS,
  customChains.swell.id,
  customChains.bartio.id,
  chains.sepolia.id,
  chains.foundry.id,
  customChains.tenderly.id, //tenderly custom id
  customChains.sonic.id,
  customChains.berachain.id,
  customChains.ink.id,
  customChains.unichain.id,
  chains.avalanche.id,
  customChains.bob.id,
  chains.bsc.id,
  customChains.hyperevm.id,
  customChains.tac.id,
  chains.linea.id,
  chains.mantle.id,
  customChains.plasma.id,
  customChains.monad.id,
  // Generator Helper: @SupportedChainIds
] as const

export const SUPPORTED_CRONS_CHAIN_ID = [
  chains.mainnet.id,
  chains.base.id,
  chains.arbitrum.id,
  customChains.swell.id,
  customChains.sonic.id,
  customChains.bob.id,
  customChains.berachain.id,
  chains.avalanche.id,
  chains.bsc.id,
  customChains.unichain.id,
  customChains.hyperevm.id,
  customChains.tac.id,
  chains.linea.id,
  chains.mantle.id,
  customChains.plasma.id,
  customChains.monad.id,
  // customChains.ink.id,
] as const

export const CRON_CHAINS: Record<SupportedCronChainId, chains.Chain> = {
  [chains.mainnet.id]: chains.mainnet,
  [chains.arbitrum.id]: chains.arbitrum,
  [chains.base.id]: chains.base,
  [customChains.swell.id]: customChains.swell,
  [customChains.sonic.id]: customChains.sonic,
  [customChains.bob.id]: customChains.bob,
  [customChains.berachain.id]: customChains.berachain,
  [chains.avalanche.id]: chains.avalanche,
  [chains.bsc.id]: chains.bsc,
  [customChains.unichain.id]: customChains.unichain,
  [customChains.hyperevm.id]: customChains.hyperevm,
  [customChains.tac.id]: customChains.tac,
  [chains.linea.id]: chains.linea,
  [chains.mantle.id]: chains.mantle,
  [customChains.plasma.id]: customChains.plasma,
  [customChains.monad.id]: customChains.monad,
  // [customChains.ink.id]: customChains.ink,
}

export type SupportedChainId = typeof SUPPORTED_CHAIN_IDS[number]
export type SupportedMainChainId = typeof MAIN_CHAIN_IDS[number]
export type SupportedCronChainId = typeof SUPPORTED_CRONS_CHAIN_ID[number]

export const isSupportedCronChainId = (
  chainId: number,
): chainId is SupportedCronChainId =>
  SUPPORTED_CRONS_CHAIN_ID.includes(Number(chainId) as SupportedCronChainId)

export const isSupportedChainId = (
  chainId: number,
): chainId is SupportedChainId =>
  SUPPORTED_CHAIN_IDS.includes(Number(chainId) as SupportedChainId)

export const isSupportedMainChainId = (
  chainId: number,
): chainId is SupportedMainChainId =>
  MAIN_CHAIN_IDS.includes(Number(chainId) as SupportedMainChainId)

// Chain by slug
function generateSlugByName(name: string): string {
  return name.toLowerCase().replaceAll(" ", "")
}

export function getChainIdBySlug(slug: string): number {
  const chain = SUPPORTED_CHAINS.find(
    (chain) => generateSlugByName(chain.name) === slug,
  )
  if (chain && isSupportedChainId(chain?.id)) {
    return chain.id
  }

  // Invalid chainId
  return 0
}

export function getSlugByChainId(chainId: number): string {
  const chain = SUPPORTED_CHAINS.find((chain) => chain.id === chainId)
  if (chain) {
    return generateSlugByName(chain.name)
  }
  return ""
}
